// Shared, pure analysis engine used by the internal Project Analysis page,
// the comparison view, and the public client-facing share page — so every
// surface computes identical numbers.

export const round = (n) => Math.round(Number(n) || 0);
export const aedStr = (n) => 'AED ' + round(n).toLocaleString('en-GB');
export const ilsStr = (n) => '₪' + round(n).toLocaleString('en-GB');
export const eurStr = (n) => '€' + round(n).toLocaleString('en-GB');
export const usdStr = (n) => '$' + round(n).toLocaleString('en-GB');
export const pct = (n) => (Number(n) || 0).toFixed(1) + '%';

// Area helpers — one sqft→sqm conversion, used on every surface so the numbers match.
export const SQFT_TO_SQM = 0.092903;
export const sqm = (sqft) => (Number(sqft) || 0) * SQFT_TO_SQM;
// "350 sqft (32.5 m²)" — sqft rounded to whole, sqm to 1 decimal.
export const areaStr = (sqft) =>
  `${round(sqft).toLocaleString('en-GB')} sqft (${sqm(sqft).toFixed(1)} m²)`;
// AED per m² from an AED-per-sqft figure (1 sqft = 0.092903 m² ⇒ divide).
export const perSqmStr = (perSqft) => 'AED ' + round((Number(perSqft) || 0) / SQFT_TO_SQM).toLocaleString('en-GB') + '/m²';
// Full "Internal … · Balcony … · Total …" breakdown. Balcony/Total omitted when balcony = 0.
export function areaBreakdown(internal, balcony, lang = 'en') {
  const he = lang === 'he';
  const L = he ? { internal: 'פנימי', balcony: 'מרפסת', total: 'סה״כ' } : { internal: 'Internal', balcony: 'Balcony', total: 'Total' };
  const b = Number(balcony) || 0;
  const parts = [`${L.internal} ${areaStr(internal)}`];
  if (b > 0) {
    parts.push(`${L.balcony} ${areaStr(b)}`);
    parts.push(`${L.total} ${areaStr((Number(internal) || 0) + b)}`);
  }
  return parts.join(' · ');
}

export function addMonths(d, m) { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; }
export function monthYear(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

// Fallback STR expense defaults (the editable presets live in backend str_config.json).
export const STR_DEFAULTS = {
  occupancy: 75, mgmtPct: 20, dewaMonthly: 800, gasMonthly: 100,
  internetMonthly: 300, chillerMonthly: 500, dtcmLicenseYearly: 1500,
  tourismPerNight: 15, furnitureRefreshPct: 10, miscYearly: 0,
};

// opts = { dldEnabled, adminFees, scRate, startRent, capBands:[3], rentBands:[3], maturityYears,
//          strategy:'ltr'|'str', furnishingCost, str:{ adr, occupancy, mgmtPct, ... } }
export function computeAnalysis(project, unit, opts) {
  if (!project || !unit) return null;
  const { dldEnabled, adminFees, scRate, startRent, capBands, rentBands } = opts;
  // Investment maturity horizon (years from handover). Default 10.
  const maturity = Math.max(1, Math.round(Number(opts.maturityYears) || 10));
  // Rental strategy — 'str' switches net-income, yields, schedule and projections
  // to the short-term (holiday-home) model; 'ltr' keeps the existing behaviour.
  const isStr = opts.strategy === 'str';

  const price = Number(unit.price_aed) || 0;
  const internal = Number(unit.internal_sqft) || 0;
  const balcony = Number(unit.balcony_sqft) || 0;
  const totalSqft = internal + balcony;
  const emirate = project.emirate || 'dubai';

  // --- Market value / discount-to-market ---
  // The user may enter EITHER a total market value OR an average market price/sqft;
  // the other is derived from internal sqft. When present, appreciation compounds
  // from market value and the discount to purchase price is "instant equity".
  const marketValueInput = Number(unit.market_value_aed) || 0;
  const marketPsfInput = Number(unit.market_price_per_sqft) || 0;
  let marketValue = 0;
  if (marketValueInput > 0) marketValue = marketValueInput;
  else if (marketPsfInput > 0 && internal > 0) marketValue = marketPsfInput * internal;
  const hasMarket = marketValue > 0;
  const baseValue = hasMarket ? marketValue : price;                 // appreciation base
  const marketPricePerSqft = internal > 0 ? marketValue / internal : 0;
  const discountAmount = hasMarket ? (marketValue - price) : 0;      // day-1 / instant equity
  const discountPct = (hasMarket && marketValue > 0) ? (marketValue - price) / marketValue * 100 : 0;

  const today = new Date();
  const purchaseDate = today;
  const purchaseYear = today.getFullYear();

  const scYearly = scRate * internal + scRate * 0.25 * balcony;
  const dldRate = Number(opts.dldRate) > 0 ? Number(opts.dldRate) : 4;
  const dldAmount = dldEnabled ? (dldRate / 100) * price : 0;
  const admin = Number(adminFees) || 0;
  // One-time furnishing cost (STR only) — added to total investment and shown as a
  // fee-style row at handover. Resolved value is always kept for the compare view.
  const furnishingCostResolved = Number(opts.furnishingCost) || 0;
  const furnishingCost = isStr ? furnishingCostResolved : 0;
  const totalFees = dldAmount + admin + furnishingCost;
  const totalCost = price + totalFees;

  const rawPlan = (unit.payment_plans && unit.payment_plans.length)
    ? unit.payment_plans : (project.payment_plans || []);
  const plan = [...rawPlan].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const hasDates = plan.some(m => !!m.due_date);

  // A dated Handover milestone drives the projection handover date (rent start,
  // service-charge start), overriding the project's handover_date field.
  const handoverMile = plan.find(m => m.is_handover);
  const handoverDate = handoverMile?.due_date
    ? new Date(handoverMile.due_date)
    : (project.handover_date ? new Date(project.handover_date) : addMonths(today, 24));
  const handoverYear = handoverDate.getFullYear();
  const constructionYears = Math.max(0, handoverYear - purchaseYear);
  const horizon = constructionYears + maturity;

  const mDate = (m) => {
    if (m.due_date) return new Date(m.due_date);
    if (m.months_from_booking !== null && m.months_from_booking !== undefined && m.months_from_booking !== '')
      return addMonths(purchaseDate, Number(m.months_from_booking));
    if (m.is_handover) return handoverDate;
    if (m.is_post_handover) return addMonths(handoverDate, 12);
    return purchaseDate;
  };

  // payment (non-fee) rows, each with a resolved date + ordering seq
  const pay = plan.map((m, idx) => ({
    label: m.milestone_label, pctVal: Number(m.percentage) || 0,
    amount: (Number(m.percentage) || 0) / 100 * price, date: mDate(m),
    isFee: false, isHandover: !!m.is_handover, isPost: !!m.is_post_handover,
    seq: (m.sort_order ?? idx),
    // bucket by flag: Handover milestone → on-handover, PHPP flag → post, else → until
    bucket: m.is_handover ? 'onHandover' : (m.is_post_handover ? 'post' : 'until'),
  }));

  // Chronological ordering when explicit dates exist; otherwise keep authored order.
  const orderFn = hasDates
    ? (a, b) => (a.date - b.date) || (a.seq - b.seq)
    : (a, b) => a.seq - b.seq;
  const ordered = [...pay].sort(orderFn);

  const feeRows = [];
  if (admin > 0) {
    // admin is due at booking → before handover
    const firstNonPost = ordered.find(r => !r.isPost) || ordered[0];
    feeRows.push({ label: 'Admin Fee', pctVal: null, amount: admin, date: purchaseDate,
      isFee: true, seq: (firstNonPost ? firstNonPost.seq : 0) + 0.5, bucket: 'until' });
  }
  if (dldAmount > 0) {
    if (emirate === 'rak') {
      // RAK: DLD due at the dated handover milestone → counts on handover
      const hoRow = ordered.find(r => r.isHandover);
      feeRows.push({ label: `DLD ${dldRate}% (handover)`, pctVal: null, amount: dldAmount,
        date: hoRow ? hoRow.date : handoverDate, isFee: true, seq: (hoRow ? hoRow.seq : 999) + 0.5,
        bucket: 'onHandover' });
    } else {
      // Dubai: DLD due at the (dated) 20% downpayment milestone
      let cum = 0, anchor = null;
      for (const r of ordered) { cum += r.pctVal; if (cum >= 20) { anchor = r; break; } }
      if (!anchor) anchor = ordered[ordered.length - 1] || null;
      feeRows.push({ label: `DLD ${dldRate}% (20% milestone)`, pctVal: null, amount: dldAmount,
        date: anchor ? anchor.date : purchaseDate, isFee: true, seq: (anchor ? anchor.seq : 0) + 0.5,
        bucket: anchor?.isHandover ? 'onHandover' : 'until' });
    }
  }
  if (furnishingCost > 0) {
    // Furnishing is a one-time cost at handover (STR mode) → counts on handover.
    const hoRow = ordered.find(r => r.isHandover);
    feeRows.push({ label: 'Furnishing (STR)', pctVal: null, amount: furnishingCost,
      date: hoRow ? hoRow.date : handoverDate, isFee: true, isFurnishing: true,
      seq: (hoRow ? hoRow.seq : 998) + 0.6, bucket: 'onHandover' });
  }

  const rows = [...pay, ...feeRows].sort(orderFn);

  // Three payment buckets (not two):
  //   until       = milestones BEFORE handover      (+ fees due before handover)
  //   onHandover  = milestone(s) flagged Handover    (+ fees due at handover, e.g. RAK DLD)
  //   post        = milestones flagged PHPP only
  let cumAED = 0, cumPct = 0;
  let bucketUntil = 0, bucketOnHandover = 0, bucketPost = 0;
  const schedule = rows.map(r => {
    cumAED += r.amount;
    if (r.pctVal != null) cumPct += r.pctVal;
    if (r.bucket === 'post') bucketPost += r.amount;
    else if (r.bucket === 'onHandover') bucketOnHandover += r.amount;
    else bucketUntil += r.amount;
    return { ...r, cumAED, cumPct };
  });
  const grandTotal = cumAED;
  const hasPhpp = !!project.is_phpp || rows.some(r => r.bucket === 'post');
  // legacy aliases (two-bucket) kept for any external consumer
  const totalUntilHandover = bucketUntil + bucketOnHandover;
  const totalAfterHandover = bucketPost;

  // Fee transparency: which fees actually sit inside each bucket, so the summary-card
  // captions reflect reality (DLD toggled off → not listed; RAK DLD → on-handover; etc.).
  const shortFee = (r) => r.isFurnishing ? 'furnishing' : (/dld/i.test(r.label) ? 'DLD' : (/admin/i.test(r.label) ? 'admin' : r.label));
  const feeParts = { until: [], onHandover: [] };
  for (const r of feeRows) {
    const key = r.bucket === 'onHandover' ? 'onHandover' : (r.bucket === 'post' ? null : 'until');
    if (key) feeParts[key].push({ label: shortFee(r), amount: r.amount });
  }
  const feeOrder = { DLD: 0, admin: 1, furnishing: 2 };
  const byFee = (a, b) => (feeOrder[a.label] ?? 9) - (feeOrder[b.label] ?? 9);
  feeParts.until.sort(byFee);
  feeParts.onHandover.sort(byFee);
  const feesUntil = feeParts.until.reduce((s, x) => s + x.amount, 0);
  const feesOnHandover = feeParts.onHandover.reduce((s, x) => s + x.amount, 0);

  const paymentChart = schedule.map((r, i) => ({
    name: r.label.length > 14 ? r.label.slice(0, 13) + '…' : r.label,
    dateLabel: monthYear(r.date),
    step: i + 1, cumulative: round(r.cumAED), payment: round(r.amount),
  }));

  const capRate = (i) => {
    if (i <= constructionYears) return capBands[0] / 100;
    const h = i - constructionYears;
    if (h <= 3) return capBands[0] / 100;
    if (h <= 6) return capBands[1] / 100;
    return capBands[2] / 100;
  };
  const rentGrow = (h) => (h <= 3 ? rentBands[0] : h <= 6 ? rentBands[1] : rentBands[2]) / 100;
  const investedByYear = (calYear) =>
    rows.reduce((s, r) => s + (r.date.getFullYear() <= calYear ? r.amount : 0), 0);

  // ---- Short-term rental (holiday-home) financials ----
  // ADR appreciation reuses the rent-appreciation taper. Management + tourism-dirham
  // scale with revenue/occupancy; everything else is a fixed yearly cost.
  const sp = { ...STR_DEFAULTS, ...(opts.str || {}) };
  const strOcc = Math.max(0, (Number(sp.occupancy) || 0) / 100);
  const strAdr = Number(sp.adr) || 0;
  const strOccupiedNights = 365 * strOcc;
  const strGross1 = strAdr * 365 * strOcc;
  const strMgmtRate = (Number(sp.mgmtPct) || 0) / 100;
  const strTourism = (Number(sp.tourismPerNight) || 0) * strOccupiedNights;
  const strFurnitureRefresh = furnishingCostResolved * (Number(sp.furnitureRefreshPct) || 0) / 100;
  // fixed = occupancy-independent, excluding service charge (which grows) and mgmt/tourism
  const strFixed = (Number(sp.dewaMonthly) || 0) * 12 + (Number(sp.gasMonthly) || 0) * 12
    + (Number(sp.internetMonthly) || 0) * 12 + (Number(sp.chillerMonthly) || 0) * 12
    + (Number(sp.dtcmLicenseYearly) || 0) + strFurnitureRefresh + (Number(sp.miscYearly) || 0);
  const strNetForYear = (gross, sc) => gross * (1 - strMgmtRate) - strTourism - strFixed - sc;

  // Appreciation compounds from the MARKET value (baseValue) when set, so the
  // discount to purchase price shows up as day-1 "instant equity".
  let value = baseValue, cumNetRent = 0, rentPrev = 0, scPrev = 0, strGrossPrev = 0;
  // cumulative GROSS rental income (before service charge / STR opex) — used by the
  // exit simulator so operating cost = cumGrossIncome − cumNetRent is counted once.
  let cumGrossRent = 0, cumStrGross = 0;
  const years = [];
  for (let i = 0; i <= horizon; i++) {
    const calYear = purchaseYear + i;
    if (i > 0) value = value * (1 + capRate(i));
    const ysh = i - constructionYears;
    let rent = 0, sc = 0, strGross = 0, netThisYear = 0;
    if (ysh >= 1) {
      if (ysh === 1) { rent = Number(startRent) || 0; sc = scYearly; strGross = strGross1; }
      else { rent = rentPrev * (1 + rentGrow(ysh)); sc = scPrev * 1.03; strGross = strGrossPrev * (1 + rentGrow(ysh)); }
      rentPrev = rent; scPrev = sc; strGrossPrev = strGross;
      netThisYear = isStr ? strNetForYear(strGross, sc) : (rent - sc);
      cumNetRent += netThisYear;
      cumGrossRent += rent; cumStrGross += strGross;
    }
    const invested = investedByYear(calYear);
    const instantEquity = discountAmount;            // constant discount (market − price)
    const marketAppr = value - baseValue;            // appreciation above market value
    const apprGain = value - price;                  // = instantEquity + marketAppr (gross capital gain vs full price)
    // Operating cost incurred to date (Σ service charge for LTR / Σ opex for STR =
    // grossIncome − netIncome). Retained for the informational "net out-of-pocket"
    // line and reconciliation — deliberately NOT part of cash deployed (see below).
    const opCost = (isStr ? cumStrGross : cumGrossRent) - cumNetRent;
    // Cash actually deployed by this year — the single ROI denominator, shared with the
    // Exit Scenario. = cumulative principal + fees paid to date (DLD, admin) + one-time
    // furnishing for STR; investedByYear already includes every fee/furnishing row.
    // Service charges / STR opex are EXCLUDED: they already reduce net rent (the
    // numerator), so adding them here would double-count. Once every milestone + fee is
    // paid this figure is flat (holding basis — on-handover milestone counted at its year).
    const cashDeployed = invested;
    const combinedROI = cashDeployed > 0 ? (apprGain + cumNetRent) / cashDeployed * 100 : 0;
    years.push({
      i, year: calYear, ysh,
      phase: i < constructionYears ? 'Construction' : (ysh === 0 ? 'Handover' : 'Rented'),
      value, rent, sc, strGross, netRent: netThisYear, cumNetRent,
      cumGrossRent, cumStrGross,
      invested, apprGain, instantEquity, marketAppr, opCost, cashDeployed, combinedROI,
    });
  }

  const midYears = Math.max(1, Math.round(maturity / 2));
  const atHandover = years[constructionYears];
  const atMid = years[constructionYears + midYears];
  const atMaturity = years[constructionYears + maturity];
  // legacy aliases
  const at5 = years[constructionYears + 5];
  const at10 = years[constructionYears + 10];
  // ---- PHPP: cash position at the end of the post-handover payment plan — the
  // calendar year of the LAST PHPP installment, reusing the same year-row shape as
  // atHandover/atMid/atMaturity so netOutOfPocketAt() nets rent collected by then
  // against everything paid so far (same figure the Executive Summary bullet quotes).
  let atPhppEnd = null, phppEndYear = null;
  if (hasPhpp && bucketPost > 0) {
    const postRows = schedule.filter(r => r.bucket === 'post');
    phppEndYear = postRows.reduce((m, r) => Math.max(m, r.date.getFullYear()), handoverYear);
    atPhppEnd = years.find(y => y.year === phppEndYear) || atMaturity;
  }
  // All ROI is cash-on-cash: gross capital gain (value − full price) over the cash
  // deployed by that horizon (holding basis, on-handover milestone included). This
  // is the same denominator the Exit Scenario uses, so figures are consistent.
  const cashDeployedAt = (row) => row ? (row.cashDeployed || 0) : 0;
  const roiAt = (row) => (row && row.cashDeployed > 0) ? row.apprGain / row.cashDeployed * 100 : 0;
  const combinedAt = (row) => (row && row.cashDeployed > 0) ? (row.apprGain + row.cumNetRent) / row.cashDeployed * 100 : 0;
  // ---- STR year-1 breakdown, summary + breakeven-occupancy (computed regardless of
  // selected strategy so the Compare view can show both sides) ----
  const strLineItems = [
    { key: 'management', label: 'Management company', yearly: strGross1 * strMgmtRate, variable: true },
    { key: 'dewa', label: 'DEWA (water & electricity)', yearly: (Number(sp.dewaMonthly) || 0) * 12 },
    { key: 'gas', label: 'Gas', yearly: (Number(sp.gasMonthly) || 0) * 12 },
    { key: 'internet', label: 'Internet', yearly: (Number(sp.internetMonthly) || 0) * 12 },
    { key: 'chiller', label: 'Chiller / cooling', yearly: (Number(sp.chillerMonthly) || 0) * 12 },
    { key: 'dtcm', label: 'DTCM holiday-home license', yearly: (Number(sp.dtcmLicenseYearly) || 0) },
    { key: 'tourism', label: `Tourism dirham (AED ${Number(sp.tourismPerNight) || 0}/occupied night)`, yearly: strTourism, variable: true },
    { key: 'sc', label: 'Service charge', yearly: scYearly },
    { key: 'furniture', label: `Furniture refresh reserve (${Number(sp.furnitureRefreshPct) || 0}%/yr)`, yearly: strFurnitureRefresh },
    { key: 'misc', label: 'Other / miscellaneous', yearly: (Number(sp.miscYearly) || 0) },
  ];
  const strTotalExpenses = strLineItems.reduce((s, x) => s + x.yearly, 0);
  const strNet1 = strGross1 - strTotalExpenses;            // == strNetForYear(strGross1, scYearly)
  const ltrNet1 = (Number(startRent) || 0) - scYearly;
  const strTotalCostInclFurnishing = price + dldAmount + admin + furnishingCostResolved;
  // occupancy at which STR net income equals LTR net income (year-1 basis)
  const strPerOccFull = 365 * (strAdr * (1 - strMgmtRate) - (Number(sp.tourismPerNight) || 0));
  const strBreakevenOcc = strPerOccFull > 0 ? (ltrNet1 + strFixed + scYearly) / strPerOccFull * 100 : null;
  const strPayback = (strNet1 - ltrNet1) > 0 ? furnishingCostResolved / (strNet1 - ltrNet1) : null;
  const str = {
    available: strAdr > 0,
    adr: strAdr, occupancy: Number(sp.occupancy) || 0, occupiedNights: strOccupiedNights,
    gross: strGross1, totalExpenses: strTotalExpenses, net: strNet1, lineItems: strLineItems,
    furnishingCost: furnishingCostResolved, totalCostInclFurnishing: strTotalCostInclFurnishing,
    grossYield: strTotalCostInclFurnishing > 0 ? strGross1 / strTotalCostInclFurnishing * 100 : 0,
    netYield: strTotalCostInclFurnishing > 0 ? strNet1 / strTotalCostInclFurnishing * 100 : 0,
    ltrNet: ltrNet1, extraVsLtr: strNet1 - ltrNet1, breakevenOcc: strBreakevenOcc,
    payback: strPayback, params: sp,
  };

  // Conventional market-benchmark yields (kept alongside the cash-on-cash figure):
  //   gross = annual rent / price,  net = annual net rent / total cost incl. fees.
  const grossYield = price > 0 ? ((isStr ? strGross1 : (Number(startRent) || 0)) / price * 100) : 0;
  const netYield = totalCost > 0 ? ((isStr ? strNet1 : ((Number(startRent) || 0) - scYearly)) / totalCost * 100) : 0;
  // Cash-on-cash rental yield(T): time-matched net annual rent over cash deployed by T.
  // Before full deployment cash deployed < total cost, so this sits ABOVE the net yield
  // and converges down to it once all capital is in. The headline pins the first full
  // year after handover — year-1 net rent on cash deployed at handover.
  const annualNetRent = isStr ? strNet1 : ((Number(startRent) || 0) - scYearly);
  const cashYieldAt = (row) => (row && row.cashDeployed > 0) ? annualNetRent / row.cashDeployed * 100 : 0;
  const cashYield = cashYieldAt(atHandover);
  // Informational only (never a ROI input): the true net cash still out of pocket after
  // banking gross rent = cash deployed + operating cost paid (service charge / STR opex)
  // − gross rent collected, by horizon T.
  const netOutOfPocketAt = (row) => row
    ? (cashDeployedAt(row) + (row.opCost || 0) - (isStr ? row.cumStrGross : row.cumGrossRent)) : 0;
  const breakEven = years.find(y => (y.apprGain + y.cumNetRent) >= totalFees);
  // ---- Exit scenarios (resale simulator) ----
  // Time-aware sell-at-T model, T = handover + n years (n = 0 is the classic flip).
  // Profit is the GROSS capital gain plus net rent — fees & service charges are NOT
  // deducted from profit; they live only in the cash-invested (ROI denominator) line.
  //   exit_profit  = sale value − full purchase price + net rent collected
  //                = sale value − outstanding − principal paid + net rent
  //   net_after    = exit_profit − fees − furnishing   (the true economic profit; service
  //                  charge / STR opex is already netted inside net rent)
  //   cash_invested = principal paid by T + fees (+ one-time furnishing for STR)
  //                   ← ROI denominator; service charge / STR opex EXCLUDED (already
  //                   netted inside net rent, so counting them here would double-count)
  //   ROI on cash  = exit_profit / cash_invested
  const feesPaid = dldAmount + admin;                    // due by handover → always paid
  const furnishOneTime = isStr ? furnishingCostResolved : 0;
  const payRows = schedule.filter(r => !r.isFee);        // milestone (principal) rows
  const firstPaymentDate = schedule.reduce(
    (min, r) => (r.date < min ? r.date : min), schedule.length ? schedule[0].date : purchaseDate);
  const YEAR_MS = 365.25 * 24 * 3600 * 1000;
  // Combined ROI at maturity, annualized from the first payment date (geometric —
  // same method as the Exit Scenario's annualized ROI).
  const _matAsOf = addMonths(handoverDate, 12 * maturity);
  const _yrsToMat = Math.max(1e-9, (_matAsOf - firstPaymentDate) / YEAR_MS);
  const _combinedMat = combinedAt(atMaturity);
  const combinedMaturityAnnual = (1 + _combinedMat / 100) > 0
    ? (Math.pow(1 + _combinedMat / 100, 1 / _yrsToMat) - 1) * 100 : _combinedMat;
  const exitScenarios = [];
  for (let n = 0; n <= maturity; n++) {
    const row = years[constructionYears + n];
    if (!row) break;
    const exitDate = addMonths(handoverDate, 12 * n);
    // n === 0: flip AT handover, before completion — only strictly pre-handover
    // milestones are paid; on-handover + PHPP settle from the sale. n >= 1: everything
    // due on or before the exit date has been paid.
    const isPaid = (d) => (n === 0 ? d < handoverDate : d <= exitDate);
    let paidMilestones = 0, outstanding = 0, remainingInstallments = 0;
    for (const r of payRows) {
      if (isPaid(r.date)) paidMilestones += r.amount;
      else { outstanding += r.amount; remainingInstallments += 1; }
    }
    const grossIncome = isStr ? row.cumStrGross : row.cumGrossRent;   // handover→T
    const netRent = row.cumNetRent;                                   // net of SC / STR opex
    const opCost = grossIncome - netRent;                            // Σ SC (LTR) / Σ opex (STR)
    const saleValue = row.value;
    const cashInvested = paidMilestones + feesPaid + furnishOneTime;   // ROI denominator — payments + fees (+ STR furnishing); SC / opex excluded
    const profit = saleValue - price + netRent;                     // gross capital gain + net rent
    const netAfter = profit - feesPaid - furnishOneTime;            // after fees & service charges
    const roi = cashInvested > 0 ? profit / cashInvested * 100 : 0;
    const yrsFromFirst = Math.max(1e-9, (exitDate - firstPaymentDate) / YEAR_MS);
    const base = 1 + roi / 100;
    const annualizedRoi = base > 0 ? (Math.pow(base, 1 / yrsFromFirst) - 1) * 100 : roi;
    exitScenarios.push({
      n, exitDate, exitYear: row.year, saleValue, purchasePrice: price,
      paidMilestones, fees: feesPaid, serviceCharges: opCost, furnishing: furnishOneTime,
      cashInvested, outstanding, remainingInstallments, rentIn: grossIncome, netRent,
      profit, netAfter, roi, annualizedRoi, yearsHeld: n, yearsFromFirstPayment: yrsFromFirst,
    });
  }
  // Flip (sell at handover) aliases — n = 0 scenario, kept for existing consumers.
  const flip0 = exitScenarios[0] || null;
  const flipProfit = flip0 ? flip0.profit : 0;
  const flipROI = flip0 ? flip0.roi : 0;
  const cashDeployed = flip0 ? flip0.cashInvested : bucketUntil;
  const outstandingBalance = flip0 ? flip0.outstanding : (bucketOnHandover + bucketPost);
  const flipHandoverValue = flip0 ? flip0.saleValue : (atHandover?.value || baseValue);
  const paidUntilHandover = bucketUntil + bucketOnHandover;
  // Price per sqft is on TOTAL sellable area (internal + balcony), the Dubai listing convention.
  const pricePerSqft = totalSqft > 0 ? price / totalSqft : 0;

  return {
    price, internal, balcony, totalSqft, pricePerSqft, scYearly, dldAmount, dldRate, admin, totalFees, totalCost, emirate,
    handoverDate, constructionYears, schedule, grandTotal, hasDates,
    bucketUntil, bucketOnHandover, bucketPost, hasPhpp,
    feeParts, feesUntil, feesOnHandover,
    totalUntilHandover, totalAfterHandover, paymentChart,
    marketValue, marketPricePerSqft, hasMarket, discountAmount, discountPct, instantEquity: discountAmount,
    maturityYears: maturity, midYears,
    years, atHandover, atMid, atMaturity, at5, at10, atPhppEnd, phppEndYear,
    roiAt, combinedAt, cashDeployedAt, cashYieldAt, cashYield, annualNetRent, netOutOfPocketAt,
    grossYield, netYield, combinedMaturityAnnual, combined10Annual: combinedMaturityAnnual,
    breakEven, flipProfit, flipROI, paidUntilHandover, startRent: Number(startRent) || 0,
    flipHandoverValue, outstandingBalance, cashDeployed, exitScenarios,
    strategy: isStr ? 'str' : 'ltr', furnishingCost, str,
  };
}

// Exact-date formatters for the client-facing summary strip.
export const dmy = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');                                  // DD/MM/YYYY
export const my = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { month: '2-digit', year: 'numeric' }) : '—'); // MM/YYYY

// ---- Period-by-period cash flow ----
// The internal calculation is DATE-LEVEL: a ledger of individual dated events —
// each milestone / fee on its exact due_date, rent as monthly income events from
// handover, and service charge / STR opex as monthly cost events (per-year growth
// already baked into a.years). Cumulative net position is accumulated event-by-event
// on this chronologically-sorted ledger, and the peak cash requirement / cash
// break-even are read straight off it (exact dates), NOT off a quarterly/yearly row.
//
// The quarterly / yearly / monthly tables below are pure AGGREGATIONS of that ledger
// (sum of events falling in each period) — never a parallel calculation. Money-IN is
// GROSS rent (LTR) or GROSS STR revenue (STR); every cost lives in the two money-OUT
// columns (payments + ownership), so per-period net = rentIn − payments − ownership
// ties back to the engine's net rent and reconciles with the Exit Scenario at the
// exact exit date (assertion below). Reuses a.schedule, a.years and a.exitScenarios —
// no formula is re-derived here (single source of truth).
export function computeCashFlow(a) {
  if (!a || !a.schedule || !a.schedule.length) return null;
  const isStr = a.strategy === 'str';
  const handover = new Date(a.handoverDate);

  // ---------- 1) DATED EVENT LEDGER (single source of truth) ----------
  // Each event: { date, kind, flow, amount(signed: in +, out −), label, meta… }.
  //   flow 'payment'   → milestone / DLD / admin fee (money out)
  //   flow 'ownership' → service charge, STR opex, one-time furnishing (money out)
  //   flow 'rent'      → gross rent / gross STR revenue (money in)
  const events = [];

  // milestone + fee payments on their exact dates; the one-time STR furnishing cost
  // is an ownership cost booked on its own date (handover), not a milestone.
  for (const r of a.schedule) {
    const furnish = !!r.isFurnishing;
    events.push({
      date: new Date(r.date),
      kind: furnish ? 'furnish' : 'payment',
      flow: furnish ? 'ownership' : 'payment',
      amount: -r.amount,
      label: furnish ? 'Furnishing (one-time)' : r.label,
      isFee: !!r.isFee, isPost: !!r.isPost, isHandover: !!r.isHandover, isFurnishing: furnish,
    });
  }

  // rent (in) + service charge / STR opex (out) as MONTHLY events from the handover
  // date. Rental year `ysh` (a.years, ysh >= 1) spans months [handover + 12(ysh−1) ..
  // +12·ysh − 1]; the per-year figure already carries that year's growth, so monthly
  // = yearly / 12. This makes exit n (= handover + 12n) land exactly on the boundary
  // between rental year n and n+1 — n full years of income sit strictly before it.
  for (const y of a.years) {
    if (y.ysh < 1) continue;
    const grossYr = isStr ? y.strGross : y.rent;
    const scYr = y.sc;
    const opexYr = isStr ? Math.max(0, y.strGross - y.netRent - y.sc) : 0;
    for (let j = 0; j < 12; j++) {
      const d = addMonths(handover, 12 * (y.ysh - 1) + j);
      if (grossYr) events.push({ date: d, kind: 'rent', flow: 'rent', amount: grossYr / 12, label: isStr ? 'STR revenue' : 'Rent' });
      if (scYr) events.push({ date: d, kind: 'sc', flow: 'ownership', amount: -scYr / 12, label: 'Service charge' });
      if (opexYr) events.push({ date: d, kind: 'strOpex', flow: 'ownership', amount: -opexYr / 12, label: 'STR operating costs' });
    }
  }

  // chronological order; on a same-day tie, outflows before inflows so the cumulative
  // dip is captured on the day money actually leaves (conservative peak requirement).
  const rank = { payment: 0, furnish: 0, sc: 1, strOpex: 1, rent: 2 };
  events.sort((e1, e2) => (e1.date - e2.date) || (rank[e1.kind] - rank[e2.kind]));

  // cumulative net position, event by event, on the dated ledger.
  let cum = 0;
  for (const e of events) { cum += e.amount; e.cumulative = cum; }
  const endCumulative = events.length ? events[events.length - 1].cumulative : 0;

  // peak cash requirement = most-negative cumulative point across the DATED ledger.
  let peakE = events[0];
  for (const e of events) if (e.cumulative < peakE.cumulative) peakE = e;
  const peak = {
    amount: peakE.cumulative, magnitude: -peakE.cumulative,
    date: peakE.date, label: dmy(peakE.date), monthLabel: my(peakE.date),
    year: peakE.date.getFullYear(),
  };

  // cash break-even = the exact date the cumulative ledger first crosses >= 0.
  const beE = events.find(e => e.cumulative >= 0) || null;

  // ---------- 2) DISPLAY AGGREGATIONS (sum of ledger events per period) ----------
  const qIndex = (d) => d.getFullYear() * 4 + Math.floor(d.getMonth() / 3);
  const qOf = (idx) => ({ year: Math.floor(idx / 4), q: (idx % 4) + 1 });
  const qKey = (idx) => { const { year, q } = qOf(idx); return `${year}Q${q}`; };
  const qLabel = (idx) => { const { year, q } = qOf(idx); return `Q${q} ${year}`; };
  const handoverIdx = qIndex(handover);

  // Fold the sorted ledger into periods. Each row sums its events; cumulative is the
  // running total at the row's last event (events are already chronological).
  const aggregate = (keyOf, metaOf) => {
    const map = new Map(); const order = [];
    for (const e of events) {
      const k = keyOf(e.date);
      let row = map.get(k);
      if (!row) {
        row = { payments: 0, ownership: 0, rentIn: 0, net: 0, cumulative: 0,
          paymentItems: [], _own: {}, ...metaOf(e.date) };
        map.set(k, row); order.push(k);
      }
      if (e.flow === 'payment') {
        row.payments += -e.amount;
        row.paymentItems.push({ label: e.label, amount: -e.amount, isFee: e.isFee, isPost: e.isPost, isHandover: e.isHandover });
      } else if (e.flow === 'ownership') {
        row.ownership += -e.amount;
        row._own[e.label] = (row._own[e.label] || 0) + (-e.amount);
      } else {
        row.rentIn += e.amount;
      }
      row.net += e.amount;               // signed sum = rentIn − payments − ownership
      row.cumulative = e.cumulative;     // last event in the period wins
    }
    return order.map(k => {
      const r = map.get(k);
      r.ownershipItems = Object.entries(r._own).map(([label, amount]) => ({ label, amount }));
      delete r._own;
      return r;
    });
  };

  const quarters = aggregate(
    (d) => qIndex(d),
    (d) => { const idx = qIndex(d), { year, q } = qOf(idx);
      return { idx, key: qKey(idx), label: qLabel(idx), year, q, dateStart: new Date(year, (q - 1) * 3, 1),
        isHandover: idx === handoverIdx, phase: idx < handoverIdx ? 'pre' : (idx === handoverIdx ? 'handover' : 'post') }; });

  const yearly = aggregate(
    (d) => d.getFullYear(),
    (d) => ({ year: d.getFullYear(), label: String(d.getFullYear()), hasHandover: d.getFullYear() === handover.getFullYear() }));

  const monthly = aggregate(
    (d) => d.getFullYear() * 12 + d.getMonth(),
    (d) => ({ year: d.getFullYear(), month: d.getMonth(), key: my(d), label: monthYear(d),
      dateStart: new Date(d.getFullYear(), d.getMonth(), 1),
      isHandover: d.getFullYear() === handover.getFullYear() && d.getMonth() === handover.getMonth() }));

  // break-even mapped onto the display grains (for the chart reference dot).
  const beQidx = beE ? qIndex(beE.date) : null;
  const breakEven = beE ? {
    date: beE.date, label: my(beE.date), dmy: dmy(beE.date), monthLabel: monthYear(beE.date),
    quarterKey: qKey(beQidx), quarterLabel: qLabel(beQidx), year: beE.date.getFullYear(),
  } : null;
  const beY = beE ? (yearly.find(y => y.cumulative >= 0) || null) : null;

  const cf = {
    strategy: a.strategy, events, quarters, yearly, monthly, peak,
    breakEven,
    breakEvenYear: beY ? { label: beY.label, year: beY.year } : null,
    breakEvenReached: !!beE,
    endCumulative,
    handoverIdx, handoverKey: qKey(handoverIdx), handoverLabel: qLabel(handoverIdx),
    handoverMonthLabel: monthYear(handover), handoverYear: qOf(handoverIdx).year,
    maturityYears: a.maturityYears,
  };
  cf.reconciliation = reconcileCashFlow(a, cf);
  return cf;
}

// Internal consistency check tying the cash-flow cumulative to the Exit Scenario,
// run against the DATED ledger at each exit's exact date. For every hold-and-sell
// exit (n >= 1) the following identity must hold:
//   cumulative_at(exit date) + sale value − outstanding = exit net-after-fees
// (both sides are the true economic profit; algebra: cumulative = cumNetRent −
//  cashInvested, and price = paid milestones + outstanding). n = 0 (flip before
// completion) is excluded — the handover milestone settles from the sale, not cash.
function reconcileCashFlow(a, cf) {
  const scenarios = a.exitScenarios || [];
  const events = cf.events;
  let maxError = 0; const rows = [];
  for (const s of scenarios) {
    if (s.n < 1) continue;
    // Cumulative on the dated ledger at the exact exit date. Rent / service-charge /
    // STR-opex events dated exactly on the exit date open the NEXT rental period
    // (income not yet earned) → excluded; milestone / furnishing payments due on or
    // before the exit date are paid → included. Matches the Exit Scenario's isPaid.
    const t = s.exitDate.getTime();
    let cumAt = 0;
    for (const e of events) {
      const et = e.date.getTime();
      const income = (e.kind === 'rent' || e.kind === 'sc' || e.kind === 'strOpex');
      if (income ? et < t : et <= t) cumAt += e.amount;
    }
    const lhs = cumAt + s.saleValue - s.outstanding;
    const err = Math.abs(lhs - s.netAfter);
    if (err > maxError) maxError = err;
    rows.push({ year: s.exitYear, date: s.exitDate, lhs, rhs: s.netAfter, err });
  }
  const ok = maxError < 1.0;                                // AED — allow sub-currency rounding drift
  if (!ok && typeof console !== 'undefined' && console.warn) {
    console.warn('[cashflow] Exit-scenario reconciliation drift of', maxError, 'AED', rows);
  }
  return { ok, maxError, rows };
}

// ---- Executive summary ----
// Plain-language bullets generated straight from the computed metrics, so they always
// match the current assumptions (no hand-written copy). Each bullet is { icon, label,
// segments }, where segments is [{ t, b? }] — `b: true` marks a metric to render bold.
// Shared verbatim by the internal page, the client share page and the PDFs.
export function buildExecutiveSummary(a, ils, lang = 'en') {
  if (!a) return { bullets: [], disclaimer: '' };
  const he = lang === 'he';
  const rate = Number(ils) || 0;
  const isStr = a.strategy === 'str';
  const T = (t) => ({ t });          // plain segment
  const Bd = (t) => ({ t, b: true }); // bold metric segment
  const bullets = [];

  // 1) Capital requirements — cash to start + cash deployed by handover (incl. fees).
  const firstPay = a.schedule.find(r => !r.isFee);
  const initial = (firstPay ? firstPay.amount : 0) + (a.admin || 0);   // booking + booking (admin) fee
  const byHandover = a.cashDeployedAt(a.atHandover);                   // principal + all fees paid by handover
  bullets.push({
    icon: '💰', label: he ? 'דרישות הון' : 'Capital Requirements',
    segments: he
      ? [T('הון התחלתי של '), Bd(aedStr(initial)), T(' להתחלה. סך הכל עד המסירה: '),
        Bd(aedStr(byHandover)), T(' ('), Bd(ilsStr(byHandover * rate)), T(').')]
      : [T('Initial capital of '), Bd(aedStr(initial)), T(' to start. Total until handover: '),
        Bd(aedStr(byHandover)), T(' ('), Bd(ilsStr(byHandover * rate)), T(').')],
  });

  // 2) Growth velocity — annualized combined ROI + rule-of-72 doubling time.
  const roi = a.combinedMaturityAnnual;
  const seg2 = he ? [T('תשואה משולבת שנתית חזויה של '), Bd(pct(roi))] : [T('Projected annualized combined ROI of '), Bd(pct(roi))];
  if (roi > 0) {
    const dbl = Math.round(72 / roi);
    if (he) seg2.push(T(' — ההון מוכפל בכ-'), Bd(`${dbl} ${dbl === 1 ? 'שנה' : 'שנים'}`), T('.'));
    else seg2.push(T(' — capital doubles in approx. '), Bd(`${dbl} ${dbl === 1 ? 'year' : 'years'}`), T('.'));
  } else seg2.push(T('.'));
  bullets.push({ icon: '🚀', label: he ? 'קצב צמיחה' : 'Growth Velocity', segments: seg2 });

  // 3) Cash flow — monthly net rent (or STR net income) + year-1 cash-on-cash yield,
  //    augmented with the peak cash requirement + cash break-even from the cash-flow model.
  const monthly = a.annualNetRent / 12;
  const seg3 = he
    ? [T(`מהמסירה, ${isStr ? 'הכנסה' : 'שכר דירה'} נטו חזוי של `), Bd(`${aedStr(monthly)}/חודש`),
      T(' ('), Bd(pct(a.cashYield)), T(' על המזומן שהושקע).')]
    : [T(`From handover, projected net ${isStr ? 'income' : 'rent'} of `), Bd(`${aedStr(monthly)}/month`),
      T(' ('), Bd(pct(a.cashYield)), T(' on cash deployed).')];
  const cf = computeCashFlow(a);
  if (cf) {
    if (he) {
      seg3.push(T(' דרישת מזומן שיא '), Bd(aedStr(cf.peak.magnitude)), T(' ב-'), Bd(cf.peak.label), T('; '));
      seg3.push(T('נקודת איזון מזומן '), Bd(cf.breakEvenReached ? cf.breakEven.label : `מעבר לאופק +${a.maturityYears} ש׳`), T('.'));
    } else {
      seg3.push(T(' Peak cash requirement '), Bd(aedStr(cf.peak.magnitude)), T(' on '), Bd(cf.peak.label), T('; '));
      seg3.push(T('cash break-even '), Bd(cf.breakEvenReached ? cf.breakEven.label : `beyond +${a.maturityYears}y horizon`), T('.'));
    }
  }
  bullets.push({ icon: '🏠', label: he ? 'תזרים מזומנים' : 'Cash Flow', segments: seg3 });

  // 4) Capital at work — value vs cash deployed and total profit at maturity.
  const mv = a.atMaturity;
  const totalProfit = (mv.apprGain || 0) + (mv.cumNetRent || 0);
  bullets.push({
    icon: '📈', label: he ? 'הון בעבודה' : 'Capital at Work',
    segments: he
      ? [T('עד '), Bd(String(mv.year)), T(', שווי חזוי '), Bd(aedStr(mv.value)), T(' מול '),
        Bd(aedStr(a.cashDeployedAt(mv))), T(' שהושקע — רווח כולל '), Bd(aedStr(totalProfit)), T('.')]
      : [T('By '), Bd(String(mv.year)), T(', projected value '), Bd(aedStr(mv.value)), T(' vs '),
        Bd(aedStr(a.cashDeployedAt(mv))), T(' deployed — total profit '), Bd(aedStr(totalProfit)), T('.')],
  });

  // 5) Safety margin — break-even year + how far rent can fall before it stops covering
  // costs (service charge for LTR; all operating costs for STR).
  const gross = isStr ? a.str.gross : a.startRent;
  const cushion = gross > 0 ? Math.max(0, a.annualNetRent / gross) * 100 : 0;
  const seg5 = [];
  if (he) {
    if (a.breakEven) seg5.push(T('נקודת איזון בשנת '), Bd(String(a.breakEven.year)), T('; '));
    seg5.push(T(`${isStr ? 'ההכנסות' : 'שכר הדירה'} יכול לרדת ב-`), Bd(pct(cushion)),
      T(` ועדיין לכסות ${isStr ? 'עלויות תפעול' : 'דמי ניהול'}.`));
  } else {
    if (a.breakEven) seg5.push(T('Break-even in '), Bd(String(a.breakEven.year)), T('; '));
    seg5.push(T(`${isStr ? 'revenue' : 'rent'} could drop `), Bd(pct(cushion)),
      T(` and still cover ${isStr ? 'operating costs' : 'service charges'}.`));
  }
  bullets.push({ icon: '🛡️', label: he ? 'שולי ביטחון' : 'Safety Margin', segments: seg5 });

  // 6) PHPP only — share of post-handover installments covered by projected rent.
  if (a.hasPhpp && a.bucketPost > 0) {
    const postRows = a.schedule.filter(r => r.bucket === 'post');
    const lastPostYear = postRows.reduce((m, r) => Math.max(m, r.date.getFullYear()), a.atHandover.year);
    const row = a.years.find(y => y.year === lastPostYear) || a.atMaturity;
    const grossByThen = isStr ? row.cumStrGross : row.cumGrossRent;
    const cover = grossByThen / a.bucketPost * 100;
    bullets.push({
      icon: '🔑', label: he ? 'תוכנית במימון השוכר' : 'Tenant-funded plan',
      segments: he
        ? [T('שכר הדירה החזוי מכסה ~'), Bd(pct(cover)), T(' מהתשלומים שלאחר המסירה.')]
        : [T('Projected rent covers ~'), Bd(pct(cover)), T(' of post-handover installments.')],
    });
  }

  return {
    bullets,
    disclaimer: he
      ? 'התחזיות הן הערכות המבוססות על ההנחות שצוינו ואינן מובטחות.'
      : 'Projections are estimates based on stated assumptions and are not guaranteed.',
  };
}

// Caption for a summary card, e.g. "incl. AED 30,700 fees (DLD + admin)".
// Empty string when the bucket contains no fees.
export function feeCaption(parts, lang = 'en') {
  if (!parts || !parts.length) return '';
  const he = lang === 'he';
  const tok = he ? { admin: 'ניהול', furnishing: 'ריהוט' } : {};
  const total = parts.reduce((s, x) => s + x.amount, 0);
  const labels = [...new Set(parts.map(x => tok[x.label] || x.label))].join(' + ');
  return he ? `כולל ${aedStr(total)} עמלות (${labels})` : `incl. ${aedStr(total)} fees (${labels})`;
}

// ---- Resale costs (Exit Scenario) ----
// Optional broker commission (% of sale value) + flat NOC fee, deducted from exit
// proceeds. Applied as a pure transform on a precomputed exit scenario so the Flip
// hero card and the Exit Scenario card share one implementation and stay identical.
export const RESALE_DEFAULTS = { enabled: false, commissionPct: 2, nocFee: 5250 };
export function withResaleCosts(s, opts) {
  if (!s) return s;
  const o = opts || {};
  if (!o.enabled) return { ...s, resale: false, commission: 0, noc: 0 };
  const commission = (Number(o.commissionPct) || 0) / 100 * s.saleValue;
  const noc = Number(o.nocFee) || 0;
  const profit = s.profit - commission - noc;
  const netAfter = s.netAfter - commission - noc;
  const roi = s.cashInvested > 0 ? profit / s.cashInvested * 100 : 0;
  const base = 1 + roi / 100;
  const annualizedRoi = (s.yearsFromFirstPayment > 0 && base > 0)
    ? (Math.pow(base, 1 / s.yearsFromFirstPayment) - 1) * 100 : roi;
  return { ...s, resale: true, commission, noc, profit, netAfter, roi, annualizedRoi };
}

// ==========================================================================
// BULK DEAL — package purchase of several units (one buyer, one deal).
// Reuses the single-unit engine per line, scales each line by its quantity, and
// SUMS absolute amounts; ratios (ROI, yields, price/sqft) are recomputed at the
// package level from those sums. No new financial formula is introduced here.
// ==========================================================================

// Resolve one deal line into an effective unit + its per-unit analysis.
//   line = { project, unit, quantity, priceOverride, rentOverride, furnishingCost? }
//   opts = { capBands, rentBands, maturityYears, strategy, str, furnishingCost }
//   bulkDiscountPct — when a line has no priceOverride, effective = list × (1 − pct/100)
export function bulkLineAnalysis(line, opts, bulkDiscountPct) {
  const listPrice = Number(line.unit.price_aed) || 0;
  const hasOverride = line.priceOverride !== undefined && line.priceOverride !== null
    && line.priceOverride !== '' && Number(line.priceOverride) > 0;
  const disc = Number(bulkDiscountPct) || 0;
  const effPrice = hasOverride ? Number(line.priceOverride)
    : (disc > 0 ? listPrice * (1 - disc / 100) : listPrice);
  const rentOverride = (line.rentOverride !== undefined && line.rentOverride !== null && line.rentOverride !== '')
    ? Number(line.rentOverride) : null;
  const effUnit = {
    ...line.unit, price_aed: effPrice,
    expected_rent_yearly_aed: rentOverride != null ? rentOverride : (line.unit.expected_rent_yearly_aed || 0),
  };
  const isStr = opts.strategy === 'str';
  const ua = line.unit.assumptions;
  // Bands/maturity/strategy are the deal's OWN shared assumptions (a package is analyzed
  // under one strategy); DLD/admin/fees still come from each unit's own saved assumptions
  // — independent of both the deal and any other unit.
  const engineOpts = {
    dldEnabled: ua ? ua.dld_enabled : line.project.dld_enabled,
    dldRate: ua ? ua.dld_rate : 4,
    adminFees: (ua ? ua.admin_fees : line.project.admin_fees) || 0,
    scRate: line.unit.service_charge_rate || 14,
    startRent: effUnit.expected_rent_yearly_aed || 0,
    capBands: opts.capBands, rentBands: opts.rentBands, maturityYears: opts.maturityYears || 10,
    strategy: opts.strategy,
    furnishingCost: isStr ? (Number(line.furnishingCost) || Number(opts.furnishingCost) || 0) : 0,
    str: line.str || opts.str || {},   // per-unit STR params (ADR/occupancy) when provided
  };
  const a = computeAnalysis(line.project, effUnit, engineOpts);
  // Instant equity reference: market value when set, else the LIST price (a bulk price
  // below list IS the discount). Label which reference each line used.
  const marketRef = a.hasMarket ? a.marketValue : listPrice;
  const marketRefKind = a.hasMarket ? 'market' : 'list';
  return {
    project: line.project, unit: line.unit,
    quantity: Math.max(1, Math.round(Number(line.quantity) || 1)),
    listPrice, effPrice, hasOverride, rentOverride,
    rent: effUnit.expected_rent_yearly_aed || 0,
    a, engineOpts, marketRef, marketRefKind,
    instantEquityPer: marketRef - effPrice,
  };
}

export function computeBulkAnalysis(rawLines, opts, bulkDiscountPct) {
  const o = opts || {};
  const lines = (rawLines || [])
    .filter(l => l && l.project && l.unit)
    .map(l => bulkLineAnalysis(l, o, bulkDiscountPct));
  if (!lines.length) return null;
  const isStr = o.strategy === 'str';

  const sum = (fn) => lines.reduce((s, L) => s + L.quantity * (Number(fn(L)) || 0), 0);
  const units = lines.reduce((s, L) => s + L.quantity, 0);
  const per = (v) => (units > 0 ? v / units : 0);

  const price = sum(L => L.effPrice);
  const listTotal = sum(L => L.listPrice);
  const marketRefTotal = sum(L => L.marketRef);
  const internal = sum(L => L.a.internal);
  const balcony = sum(L => L.a.balcony);
  const totalSqft = sum(L => L.a.totalSqft);
  const bucketUntil = sum(L => L.a.bucketUntil);
  const bucketOnHandover = sum(L => L.a.bucketOnHandover);
  const bucketPost = sum(L => L.a.bucketPost);
  const totalFees = sum(L => L.a.totalFees);
  const totalCost = sum(L => L.a.totalCost);
  const dldAmount = sum(L => L.a.dldAmount);
  const admin = sum(L => L.a.admin);
  const scYearly = sum(L => L.a.scYearly);
  const grandTotal = sum(L => L.a.grandTotal);
  const instantEquity = sum(L => L.instantEquityPer);
  const annualNetRent = sum(L => L.a.annualNetRent);
  const startRent = sum(L => L.a.startRent);
  const strGross = sum(L => (L.a.str ? L.a.str.gross : 0));
  const strNet = sum(L => (L.a.str ? L.a.str.net : 0));

  const hasMarket = lines.some(L => L.a.hasMarket);
  const usesListRef = lines.some(L => L.marketRefKind === 'list');
  const usesMarketRef = lines.some(L => L.marketRefKind === 'market');
  const discountAmount = marketRefTotal - price;                 // vs the blended reference
  const discountPct = marketRefTotal > 0 ? discountAmount / marketRefTotal * 100 : 0;
  const savingsVsList = listTotal - price;                       // vs list price (the bulk discount)
  const savingsVsListPct = listTotal > 0 ? savingsVsList / listTotal * 100 : 0;
  const pricePerSqft = totalSqft > 0 ? price / totalSqft : 0;
  const hasPhpp = lines.some(L => L.a.hasPhpp) || bucketPost > 0;
  const hasDates = lines.some(L => L.a.hasDates);
  const emirate = lines[0].a.emirate;
  const handoverDate = lines[0].a.handoverDate;
  const constructionYears = lines[0].a.constructionYears;
  const maturityYears = lines[0].a.maturityYears;
  const midYears = lines[0].a.midYears;

  // ---- merged payment schedule (grouped by date + milestone label) ----
  const schedMap = new Map();
  for (const L of lines) {
    for (const r of L.a.schedule) {
      const key = new Date(r.date).getTime() + '|' + r.label + '|' + (r.isFee ? 'f' : 'p');
      let row = schedMap.get(key);
      if (!row) {
        row = { date: new Date(r.date), label: r.label, isFee: !!r.isFee,
          isHandover: !!r.isHandover, isPost: !!r.isPost, amount: 0, unitParts: {} };
        schedMap.set(key, row);
      }
      row.amount += r.amount * L.quantity;
      row.isHandover = row.isHandover || !!r.isHandover;
      row.isPost = row.isPost || !!r.isPost;
      row.unitParts[L.unit.unit_type] = (row.unitParts[L.unit.unit_type] || 0) + L.quantity;
    }
  }
  let cumS = 0;
  const schedule = [...schedMap.values()]
    .sort((a, b) => (a.date - b.date) || ((a.isFee ? 1 : 0) - (b.isFee ? 1 : 0)))
    .map(r => {
      cumS += r.amount;
      return {
        ...r,
        pctVal: r.isFee ? null : (price > 0 ? r.amount / price * 100 : null),
        cumAED: cumS, cumPct: price > 0 ? cumS / price * 100 : 0,
        units: Object.entries(r.unitParts).map(([t, q]) => `${q}× ${t}`).join(', '),
      };
    });
  const paymentChart = schedule.map((r, i) => ({
    name: r.label.length > 14 ? r.label.slice(0, 13) + '…' : r.label,
    dateLabel: monthYear(r.date), step: i + 1,
    cumulative: round(r.cumAED), payment: round(r.amount),
  }));

  // ---- aggregate the three ROI horizons ----
  const aggHorizon = (pick) => {
    let value = 0, cumNetRent = 0, cashDeployed = 0, apprGain = 0, marketAppr = 0, invested = 0, year = null;
    for (const L of lines) {
      const row = pick(L.a);
      if (!row) continue;
      value += L.quantity * row.value;
      cumNetRent += L.quantity * row.cumNetRent;
      cashDeployed += L.quantity * L.a.cashDeployedAt(row);
      apprGain += L.quantity * (row.apprGain || 0);
      marketAppr += L.quantity * (row.marketAppr || 0);
      invested += L.quantity * (row.invested || 0);
      year = row.year;
    }
    return {
      value, cumNetRent, cashDeployed, apprGain, marketAppr, invested, year,
      roi: cashDeployed > 0 ? apprGain / cashDeployed * 100 : 0,
      combined: cashDeployed > 0 ? (apprGain + cumNetRent) / cashDeployed * 100 : 0,
    };
  };
  const atHandover = aggHorizon(a => a.atHandover);
  const atMid = aggHorizon(a => a.atMid);
  const atMaturity = aggHorizon(a => a.atMaturity);

  const grossYield = price > 0 ? (isStr ? strGross : startRent) / price * 100 : 0;
  const netYield = totalCost > 0 ? annualNetRent / totalCost * 100 : 0;
  const cashYield = atHandover.cashDeployed > 0 ? annualNetRent / atHandover.cashDeployed * 100 : 0;

  // ---- merged year-by-year projection (for charts) ----
  const yearSet = Array.from(new Set(lines.flatMap(L => L.a.years.map(y => y.year)))).sort((a, b) => a - b);
  const years = yearSet.map(yr => {
    let value = 0, invested = 0, cumNetRent = 0, cashDeployed = 0, apprGain = 0, marketAppr = 0, netRent = 0, instantEq = 0;
    for (const L of lines) {
      const y = L.a.years.find(x => x.year === yr);
      if (!y) continue;
      value += L.quantity * y.value;
      invested += L.quantity * y.invested;
      cumNetRent += L.quantity * y.cumNetRent;
      cashDeployed += L.quantity * y.cashDeployed;
      apprGain += L.quantity * y.apprGain;
      marketAppr += L.quantity * (y.marketAppr || 0);
      netRent += L.quantity * y.netRent;
      instantEq += L.quantity * L.instantEquityPer;
    }
    return {
      year: yr, value, invested, cumNetRent, cashDeployed, netRent,
      apprGain, marketAppr, instantEquity: instantEq,
      combinedROI: cashDeployed > 0 ? (apprGain + cumNetRent) / cashDeployed * 100 : 0,
    };
  });

  // ---- merged cash flow (concat per-line dated ledgers, scaled by quantity) ----
  const events = [];
  for (const L of lines) {
    const cf = computeCashFlow(L.a);
    if (!cf) continue;
    for (const e of cf.events)
      events.push({ date: new Date(e.date), kind: e.kind, flow: e.flow, amount: e.amount * L.quantity, label: e.label });
  }
  let cashFlow = null;
  if (events.length) {
    const rank = { payment: 0, furnish: 0, sc: 1, strOpex: 1, rent: 2 };
    events.sort((a, b) => (a.date - b.date) || ((rank[a.kind] ?? 9) - (rank[b.kind] ?? 9)));
    let cum = 0; for (const e of events) { cum += e.amount; e.cumulative = cum; }
    let peakE = events[0]; for (const e of events) if (e.cumulative < peakE.cumulative) peakE = e;
    const beE = events.find(e => e.cumulative >= 0) || null;
    // monthly cumulative position (chart series)
    const monthMap = new Map();
    for (const e of events) {
      const k = e.date.getFullYear() * 12 + e.date.getMonth();
      monthMap.set(k, { k, label: monthYear(e.date), cumulative: round(e.cumulative) });
    }
    cashFlow = {
      events,
      peak: { amount: peakE.cumulative, magnitude: -peakE.cumulative, date: peakE.date,
        label: dmy(peakE.date), monthLabel: my(peakE.date), year: peakE.date.getFullYear() },
      breakEven: beE ? { date: beE.date, label: my(beE.date), dmy: dmy(beE.date),
        monthLabel: monthYear(beE.date), year: beE.date.getFullYear() } : null,
      breakEvenReached: !!beE,
      endCumulative: events.length ? events[events.length - 1].cumulative : 0,
      series: [...monthMap.values()].sort((a, b) => a.k - b.k),
    };
  }

  // ---- aggregate exit scenarios (resale applied PER UNIT, then summed) ----
  // 2% commission per unit sale + one NOC fee × number of units falls out naturally.
  const exitCount = Math.min(...lines.map(L => (L.a.exitScenarios || []).length)) || 0;
  const exitAt = (n, resaleOpt) => {
    const idx = Math.max(0, Math.min(n, exitCount - 1));
    let saleValue = 0, profit = 0, netAfter = 0, cashInvested = 0, outstanding = 0, netRent = 0,
      commission = 0, noc = 0, purchasePrice = 0, remainingInstallments = 0,
      exitDate = null, exitYear = null, yearsFromFirst = 1, resaleOn = false;
    for (const L of lines) {
      const base = (L.a.exitScenarios || [])[idx];
      if (!base) continue;
      const s = withResaleCosts(base, resaleOpt || RESALE_DEFAULTS);
      saleValue += L.quantity * s.saleValue;
      profit += L.quantity * s.profit;
      netAfter += L.quantity * s.netAfter;
      cashInvested += L.quantity * s.cashInvested;
      outstanding += L.quantity * s.outstanding;
      netRent += L.quantity * s.netRent;
      commission += L.quantity * (s.commission || 0);
      noc += L.quantity * (s.noc || 0);
      purchasePrice += L.quantity * s.purchasePrice;
      remainingInstallments += L.quantity * s.remainingInstallments;
      exitDate = s.exitDate; exitYear = s.exitYear; yearsFromFirst = s.yearsFromFirstPayment; resaleOn = s.resale;
    }
    const roi = cashInvested > 0 ? profit / cashInvested * 100 : 0;
    const base = 1 + roi / 100;
    const annualizedRoi = (yearsFromFirst > 0 && base > 0) ? (Math.pow(base, 1 / yearsFromFirst) - 1) * 100 : roi;
    return {
      n: idx, exitDate, exitYear, saleValue, profit, netAfter, cashInvested, outstanding, netRent,
      commission, noc, purchasePrice, remainingInstallments, roi, annualizedRoi,
      resale: resaleOn, yearsFromFirstPayment: yearsFromFirst, units,
    };
  };

  // annualized combined ROI at maturity (aggregate combined, annualized over the horizon)
  const lastExit = exitCount ? exitAt(exitCount - 1, RESALE_DEFAULTS) : { yearsFromFirstPayment: Math.max(1, maturityYears) };
  const _c = atMaturity.combined;
  const combinedMaturityAnnual = (1 + _c / 100) > 0
    ? (Math.pow(1 + _c / 100, 1 / Math.max(1e-9, lastExit.yearsFromFirstPayment)) - 1) * 100 : _c;

  return {
    lines, units, per, isStr, strategy: o.strategy || 'ltr',
    price, listTotal, marketRefTotal, internal, balcony, totalSqft, pricePerSqft,
    bucketUntil, bucketOnHandover, bucketPost, totalFees, totalCost, dldAmount, admin, scYearly,
    grandTotal, hasPhpp, hasDates, emirate, handoverDate, constructionYears, maturityYears, midYears,
    hasMarket, usesListRef, usesMarketRef, instantEquity, discountAmount, discountPct,
    savingsVsList, savingsVsListPct,
    annualNetRent, startRent, strGross, strNet, grossYield, netYield, cashYield,
    schedule, paymentChart, years, atHandover, atMid, atMaturity, combinedMaturityAnnual,
    cashFlow, exitCount, exitAt,
  };
}

// Executive-summary bullets for a bulk deal (adapts the single-unit copy to the package).
export function buildBulkExecutiveSummary(b, ils, dealName, lang = 'en') {
  if (!b) return { bullets: [], disclaimer: '' };
  const he = lang === 'he';
  const rate = Number(ils) || 0;
  const T = (t) => ({ t });
  const Bd = (t) => ({ t, b: true });
  const bullets = [];
  const unitsWord = he ? `${b.units} ${b.units > 1 ? 'יחידות' : 'יחידה'}` : `${b.units} unit${b.units > 1 ? 's' : ''}`;

  // 1) Package headline — units, total vs list, bulk discount.
  const seg1 = he
    ? [T('מכלול של '), Bd(unitsWord), T(' — סה״כ '), Bd(aedStr(b.price)), T(' ('), Bd(ilsStr(b.price * rate)), T(')')]
    : [T('Package of '), Bd(unitsWord), T(' — total '), Bd(aedStr(b.price)), T(' ('), Bd(ilsStr(b.price * rate)), T(')')];
  if (b.savingsVsList > 0) seg1.push(...(he
    ? [T(', '), Bd(`${pct(b.savingsVsListPct)} מתחת למחירון`), T(' ('), Bd(aedStr(b.savingsVsList)), T(' חיסכון).')]
    : [T(', '), Bd(`${pct(b.savingsVsListPct)} below list`), T(' ('), Bd(aedStr(b.savingsVsList)), T(' saved).')]));
  else seg1.push(T('.'));
  bullets.push({ icon: '📦', label: he ? 'המכלול' : 'The Package', segments: seg1 });

  // 2) Capital — total until handover incl. fees + per-unit equivalent.
  const byHandover = b.atHandover.cashDeployed;
  bullets.push({
    icon: '💰', label: he ? 'דרישות הון' : 'Capital Requirements',
    segments: he
      ? [T('סך המזומן עד המסירה: '), Bd(aedStr(byHandover)), T(' ('), Bd(ilsStr(byHandover * rate)), T(') — כ-'), Bd(aedStr(b.per(byHandover))), T(' ליחידה.')]
      : [T('Total cash until handover: '), Bd(aedStr(byHandover)), T(' ('), Bd(ilsStr(byHandover * rate)), T(') — about '), Bd(aedStr(b.per(byHandover))), T(' per unit.')],
  });

  // 3) Growth — annualized combined ROI at maturity.
  const roi = b.combinedMaturityAnnual;
  const seg3 = he ? [T('תשואה משולבת שנתית חזויה של '), Bd(pct(roi))] : [T('Projected annualized combined ROI of '), Bd(pct(roi))];
  if (roi > 0) {
    const dbl = Math.round(72 / roi);
    seg3.push(...(he
      ? [T(' — ההון מוכפל בכ-'), Bd(`${dbl} ${dbl === 1 ? 'שנה' : 'שנים'}`), T('.')]
      : [T(' — capital doubles in approx. '), Bd(`${dbl} ${dbl === 1 ? 'year' : 'years'}`), T('.')]));
  } else seg3.push(T('.'));
  bullets.push({ icon: '🚀', label: he ? 'קצב צמיחה' : 'Growth Velocity', segments: seg3 });

  // 4) Cash flow — combined net rent + peak cash + cash break-even.
  const monthly = b.annualNetRent / 12;
  const seg4 = he
    ? [T(`מהמסירה, ${b.isStr ? 'הכנסה' : 'שכ״ד'} נטו משולב של `), Bd(`${aedStr(monthly)}/חודש`), T(' על פני המכלול.')]
    : [T(`From handover, combined net ${b.isStr ? 'income' : 'rent'} of `), Bd(`${aedStr(monthly)}/month`), T(' across the package.')];
  if (b.cashFlow) {
    if (he) {
      seg4.push(T(' דרישת מזומן שיא '), Bd(aedStr(b.cashFlow.peak.magnitude)), T(' ב-'), Bd(b.cashFlow.peak.label), T('; '));
      seg4.push(T('נקודת איזון מזומן '), Bd(b.cashFlow.breakEvenReached ? b.cashFlow.breakEven.label : `מעבר ל-+${b.maturityYears} ש׳`), T('.'));
    } else {
      seg4.push(T(' Peak cash requirement '), Bd(aedStr(b.cashFlow.peak.magnitude)), T(' on '), Bd(b.cashFlow.peak.label), T('; '));
      seg4.push(T('cash break-even '), Bd(b.cashFlow.breakEvenReached ? b.cashFlow.breakEven.label : `beyond +${b.maturityYears}y`), T('.'));
    }
  }
  bullets.push({ icon: '🏠', label: he ? 'תזרים מזומנים' : 'Cash Flow', segments: seg4 });

  // 5) Instant equity across the package.
  if (b.instantEquity !== 0) {
    const ref = b.usesMarketRef && !b.usesListRef;
    bullets.push({
      icon: '⚡', label: he ? 'הון מיידי' : 'Instant Equity',
      segments: he
        ? [T('רכישה מתחת למחיר '), Bd(ref ? 'השוק' : 'המחירון'), T(' מעניקה הון מיידי של '), Bd(aedStr(b.instantEquity)), T(' ('), Bd(pct(b.discountPct)), T(') על פני כל היחידות.')]
        : [T('Buying below '), Bd(ref ? 'market' : 'list'), T(' price gives day-one equity of '), Bd(aedStr(b.instantEquity)), T(' ('), Bd(pct(b.discountPct)), T(') across all units.')],
    });
  }

  return {
    bullets,
    disclaimer: he
      ? 'התחזיות הן הערכות המבוססות על ההנחות שצוינו ואינן מובטחות.'
      : 'Projections are estimates based on stated assumptions and are not guaranteed.',
  };
}

// Build engine opts for a unit from a shared band set (used by comparison + share).
// DLD/admin fee come from the UNIT's own saved assumptions (falling back to the legacy
// project-level fields only for units that predate per-unit assumptions) — every unit
// keeps its own fee structure even when bands/maturity are shared across a comparison.
export function optsForUnit(project, unit, capBands, rentBands, rentOverride, maturityYears) {
  const ua = unit.assumptions;
  return {
    dldEnabled: ua ? ua.dld_enabled : project.dld_enabled,
    dldRate: ua ? ua.dld_rate : 4,
    adminFees: (ua ? ua.admin_fees : project.admin_fees) || 0,
    scRate: unit.service_charge_rate || 14,
    startRent: (rentOverride !== undefined && rentOverride !== null && rentOverride !== '')
      ? rentOverride : (unit.expected_rent_yearly_aed || 0),
    capBands, rentBands, maturityYears: maturityYears || 10,
  };
}

// Opts for a unit using its OWN fully-independent saved assumptions (bands, maturity,
// strategy, STR expenses included) — the default for Compare mode, so viewing/editing
// one unit's comparison never depends on or mutates another's.
export function optsFromUnit(project, unit, rentOverride) {
  const ua = unit.assumptions;
  const bands = ua ? ua.cap_bands : [8, 5, 3];
  const rentBands = ua ? ua.rent_bands : [5, 3, 2];
  const base = optsForUnit(project, unit, bands, rentBands, rentOverride, ua ? ua.maturity_years : 10);
  return { ...base, strategy: ua ? ua.strategy : 'ltr' };
}

// Layers this unit's OWN STR fields (ADR/occupancy/expenses) onto an already-resolved
// LTR opts object (from optsForUnit/optsFromUnit) — used by Compare/Share so a
// comparison's STR figures stay fair per unit even when appreciation bands are shared.
// furnishingCost is resolved by the caller (needs the live str-config furnishing presets).
// ADR has no sensible platform default (unlike occupancy) — callers should flag a unit
// whose own str_adr is 0/unset with unitHasStr(unit) rather than treat the 0 as real data.
export function strOptsFor(baseOpts, unit, furnishingCost) {
  const ua = unit.assumptions;
  const se = (ua && ua.str_expenses) || {};
  return {
    ...baseOpts, strategy: 'str', furnishingCost: Number(furnishingCost) || 0,
    str: { adr: Number(unit.str_adr) || 0, occupancy: Number(unit.str_occupancy) || STR_DEFAULTS.occupancy, ...se },
  };
}

export function unitHasStr(unit) {
  return Number(unit?.str_adr) > 0;
}
